import { API_BASE } from '../../config/apiBase';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, CheckCircle, XCircle, 
  Clock, Trophy, AlertTriangle, RotateCcw
} from 'lucide-react';

const API = API_BASE;

// Function to shuffle options and return new correct index
const shuffleQuestionOptions = (question) => {
  const options_en = [...question.options_en];
  const options_es = [...question.options_es];
  const correctIdx = question.correct;
  
  // Create index array and shuffle it
  const indices = options_en.map((_, i) => i);
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  
  // Apply shuffle to both language options
  const shuffled_en = indices.map(i => options_en[i]);
  const shuffled_es = indices.map(i => options_es[i]);
  
  // Find new correct index
  const newCorrectIdx = indices.indexOf(correctIdx);
  
  return {
    ...question,
    options_en: shuffled_en,
    options_es: shuffled_es,
    correct: newCorrectIdx
  };
};

// Sandbox Certification Questions - 25 questions about tools and processes
const SANDBOX_QUESTIONS = [
  {
    question_en: "What is the URL for customers to start their funding application?",
    question_es: "¿Cuál es el URL para que los clientes inicien su solicitud de financiamiento?",
    options_en: ["inspiredfunding.com/apply", "funding.inspired.com", "inspiredfunding.org/get-funding", "apply.inspiredfunding.org"],
    options_es: ["inspiredfunding.com/apply", "funding.inspired.com", "inspiredfunding.org/get-funding", "apply.inspiredfunding.org"],
    correct: 2
  },
  {
    question_en: "What are the login credentials for the EFAI portal?",
    question_es: "¿Cuáles son las credenciales de acceso para el portal EFAI?",
    options_en: ["admin/password", "efai/efai2024", "portal/access123", "inspiredshared/20182018"],
    options_es: ["admin/password", "efai/efai2024", "portal/access123", "inspiredshared/20182018"],
    correct: 3
  },
  {
    question_en: "What is 360Portal.com used for?",
    question_es: "¿Para qué se usa 360Portal.com?",
    options_en: ["Email only", "Video conferencing", "CRM - logging calls, notes, and documents", "File storage only"],
    options_es: ["Solo email", "Videoconferencias", "CRM - registrar llamadas, notas y documentos", "Solo almacenamiento de archivos"],
    correct: 2
  },
  {
    question_en: "What should you ALWAYS check before processing any application?",
    question_es: "¿Qué debes verificar SIEMPRE antes de procesar cualquier solicitud?",
    options_en: ["Weather forecast", "Stock prices", "Social media", "Ban list for prohibited customers"],
    options_es: ["Pronóstico del clima", "Precios de acciones", "Redes sociales", "Lista de prohibidos para clientes vetados"],
    correct: 3
  },
  {
    question_en: "Where do you submit live applications?",
    question_es: "¿Dónde envías solicitudes en vivo?",
    options_en: ["Physical mail", "partner.inspiredfunding.org/live-file-submission/", "Email attachment", "Fax machine"],
    options_es: ["Correo físico", "partner.inspiredfunding.org/live-file-submission/", "Adjunto de email", "Máquina de fax"],
    correct: 1
  },
  {
    question_en: "When is a spec sheet required?",
    question_es: "¿Cuándo se requiere un spec sheet?",
    options_en: ["All purchases", "Never", "Private party equipment sales only", "Online purchases only"],
    options_es: ["Todas las compras", "Nunca", "Solo ventas de equipo entre particulares", "Solo compras en línea"],
    correct: 2
  },
  {
    question_en: "What tool do you use to check fleet truck availability?",
    question_es: "¿Qué herramienta usas para verificar disponibilidad de camiones de flotilla?",
    options_en: ["Facebook marketplace", "Google search", "partner.inspiredfunding.org/fleet-truck/", "Phone calls only"],
    options_es: ["Facebook marketplace", "Búsqueda en Google", "partner.inspiredfunding.org/fleet-truck/", "Solo llamadas telefónicas"],
    correct: 2
  },
  {
    question_en: "What communication tool is used for texting customers?",
    question_es: "¿Qué herramienta de comunicación se usa para enviar textos a clientes?",
    options_en: ["Personal phone", "Telegram", "RingCentral", "Personal email"],
    options_es: ["Teléfono personal", "Telegram", "RingCentral", "Email personal"],
    correct: 2
  },
  {
    question_en: "What email system does Inspired Funding use?",
    question_es: "¿Qué sistema de email usa Inspired Funding?",
    options_en: ["Gmail", "Yahoo Mail", "Zoho Mail", "Outlook"],
    options_es: ["Gmail", "Yahoo Mail", "Zoho Mail", "Outlook"],
    correct: 2
  },
  {
    question_en: "What is the purpose of the LOI Pricer tool?",
    question_es: "¿Cuál es el propósito de la herramienta LOI Pricer?",
    options_en: ["Send emails", "Schedule meetings", "Generate Letter of Intent pricing quotes", "Track inventory"],
    options_es: ["Enviar emails", "Programar reuniones", "Generar cotizaciones de Carta de Intención", "Rastrear inventario"],
    correct: 2
  },
  {
    question_en: "What should you document after every customer interaction?",
    question_es: "¿Qué debes documentar después de cada interacción con el cliente?",
    options_en: ["Only complaints", "All details in 360Portal.com CRM", "Nothing", "Only sales"],
    options_es: ["Solo quejas", "Todos los detalles en 360Portal.com CRM", "Nada", "Solo ventas"],
    correct: 1
  },
  {
    question_en: "What happens if you process an application from a banned customer?",
    question_es: "¿Qué pasa si procesas una solicitud de un cliente prohibido?",
    options_en: ["Promotion", "Could result in disciplinary action", "Nothing", "Bonus payment"],
    options_es: ["Promoción", "Puede resultar en acción disciplinaria", "Nada", "Pago extra"],
    correct: 1
  },
  {
    question_en: "What information should you search when checking the ban list?",
    question_es: "¿Qué información debes buscar al verificar la lista de prohibidos?",
    options_en: ["Only email", "Only address", "Name, business name, phone number", "Only date of birth"],
    options_es: ["Solo email", "Solo dirección", "Nombre, nombre del negocio, número de teléfono", "Solo fecha de nacimiento"],
    correct: 2
  },
  {
    question_en: "What is the main communication channel for customer texts?",
    question_es: "¿Cuál es el canal principal de comunicación para textos con clientes?",
    options_en: ["Personal phone", "Social media DMs", "RingCentral company line", "Personal email"],
    options_es: ["Teléfono personal", "DMs de redes sociales", "Línea de empresa RingCentral", "Email personal"],
    correct: 2
  },
  {
    question_en: "Where is the business plan form located?",
    question_es: "¿Dónde se encuentra el formulario del plan de negocio?",
    options_en: ["Physical office", "DocHub link", "Email attachment", "Not available"],
    options_es: ["Oficina física", "Enlace de DocHub", "Adjunto de email", "No disponible"],
    correct: 1
  },
  {
    question_en: "What should you verify before uploading bank statements?",
    question_es: "¿Qué debes verificar antes de subir estados de cuenta bancarios?",
    options_en: ["File size only", "Color of paper", "All pages are included and readable", "Nothing special"],
    options_es: ["Solo tamaño del archivo", "Color del papel", "Todas las páginas están incluidas y son legibles", "Nada especial"],
    correct: 2
  },
  {
    question_en: "How many months of bank statements are typically required?",
    question_es: "¿Cuántos meses de estados de cuenta bancarios se requieren típicamente?",
    options_en: ["1 month", "12 months", "3-6 months", "24 months"],
    options_es: ["1 mes", "12 meses", "3-6 meses", "24 meses"],
    correct: 2
  },
  {
    question_en: "What should you include when logging a call in the CRM?",
    question_es: "¿Qué debes incluir al registrar una llamada en el CRM?",
    options_en: ["Just 'called'", "Only customer name", "Date, time, purpose, outcome, and next steps", "Nothing specific"],
    options_es: ["Solo 'llamé'", "Solo nombre del cliente", "Fecha, hora, propósito, resultado y próximos pasos", "Nada específico"],
    correct: 2
  },
  {
    question_en: "What is a confirmation number used for?",
    question_es: "¿Para qué se usa un número de confirmación?",
    options_en: ["Login", "Password reset", "Nothing", "Tracking submitted applications"],
    options_es: ["Login", "Restablecer contraseña", "Nada", "Rastrear solicitudes enviadas"],
    correct: 3
  },
  {
    question_en: "What format should uploaded documents be in?",
    question_es: "¿En qué formato deben estar los documentos subidos?",
    options_en: ["Audio only", "Clear, legible PDF or image files", "Any format", "Video only"],
    options_es: ["Solo audio", "Archivos PDF o imagen claros y legibles", "Cualquier formato", "Solo video"],
    correct: 1
  },
  {
    question_en: "What is CamionDepot?",
    question_es: "¿Qué es CamionDepot?",
    options_en: ["Insurance company", "Bank", "Fleet truck inventory source", "Gas station"],
    options_es: ["Compañía de seguros", "Banco", "Fuente de inventario de camiones de flotilla", "Gasolinera"],
    correct: 2
  },
  {
    question_en: "What should you do if a system is down?",
    question_es: "¿Qué debes hacer si un sistema no funciona?",
    options_en: ["Go home", "Ignore it", "Report to IT and use alternative methods", "Wait indefinitely"],
    options_es: ["Ir a casa", "Ignorarlo", "Reportar a IT y usar métodos alternativos", "Esperar indefinidamente"],
    correct: 2
  },
  {
    question_en: "What should be included in an email signature?",
    question_es: "¿Qué debe incluirse en una firma de email?",
    options_en: ["Just name", "Name, title, company, and contact info", "Personal phone only", "Nothing"],
    options_es: ["Solo nombre", "Nombre, título, empresa e información de contacto", "Solo teléfono personal", "Nada"],
    correct: 1
  },
  {
    question_en: "What is proper email etiquette for customer communication?",
    question_es: "¿Cuál es la etiqueta apropiada de email para comunicación con clientes?",
    options_en: ["Emojis only", "Professional greeting, clear message, proper signature", "All caps", "Abbreviations"],
    options_es: ["Solo emojis", "Saludo profesional, mensaje claro, firma apropiada", "Todo en mayúsculas", "Abreviaciones"],
    correct: 1
  },
  {
    question_en: "What should you do before final submission of an application?",
    question_es: "¿Qué debes hacer antes del envío final de una solicitud?",
    options_en: ["Skip verification", "Verify all documents complete and ban list checked", "Submit immediately", "Ask customer to check"],
    options_es: ["Saltar verificación", "Verificar que todos los documentos estén completos y lista de prohibidos verificada", "Enviar inmediatamente", "Pedir al cliente que verifique"],
    correct: 1
  }
];

export default function SandboxQuiz() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes
  const [quizStarted, setQuizStarted] = useState(false);

  const lang = user?.training_language || 'es';
  const t = (en, es) => lang === 'es' ? es : en;

  // Shuffle questions on component mount (using useMemo to keep them stable during the quiz)
  const shuffledQuestions = useMemo(() => {
    return SANDBOX_QUESTIONS.map(q => shuffleQuestionOptions(q));
  }, []);

  // Timer
  useEffect(() => {
    if (!quizStarted || showResults) return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          calculateResults();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [quizStarted, showResults]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (index) => {
    setSelectedAnswer(index);
  };

  const handleNextQuestion = () => {
    if (selectedAnswer !== null) {
      setAnswers({ ...answers, [currentQuestion]: selectedAnswer });
    }
    
    if (currentQuestion < shuffledQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(answers[currentQuestion + 1] ?? null);
    } else {
      calculateResults();
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      if (selectedAnswer !== null) {
        setAnswers({ ...answers, [currentQuestion]: selectedAnswer });
      }
      setCurrentQuestion(currentQuestion - 1);
      setSelectedAnswer(answers[currentQuestion - 1] ?? null);
    }
  };

  const calculateResults = () => {
    let correct = 0;
    const finalAnswers = { ...answers, [currentQuestion]: selectedAnswer };
    
    shuffledQuestions.forEach((q, index) => {
      if (finalAnswers[index] === q.correct) {
        correct++;
      }
    });
    
    setScore(correct);
    setShowResults(true);
    
    const percentage = (correct / shuffledQuestions.length) * 100;
    if (percentage >= 80) {
      toast.success(t('Congratulations! You passed!', '¡Felicidades! ¡Aprobaste!'));
    } else {
      toast.error(t('You need 80% to pass. Try again!', 'Necesitas 80% para aprobar. ¡Intenta de nuevo!'));
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setSelectedAnswer(null);
    setShowResults(false);
    setScore(0);
    setTimeLeft(30 * 60);
    setQuizStarted(true);
  };

  const question = shuffledQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / shuffledQuestions.length) * 100;
  const percentage = (score / shuffledQuestions.length) * 100;
  const passed = percentage >= 80;

  if (!quizStarted) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/training/sandbox')} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Back to Sandbox', 'Volver al Sandbox')}
        </Button>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              🏆 {t('Sandbox Certification Quiz', 'Quiz de Certificación del Sandbox')}
            </CardTitle>
            <CardDescription>
              {t(
                'Test your knowledge of all tools and processes before moving to production.',
                'Prueba tu conocimiento de todas las herramientas y procesos antes de pasar a producción.'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-white rounded-lg">
                <p className="text-2xl font-bold">{shuffledQuestions.length}</p>
                <p className="text-sm text-muted-foreground">{t('Questions', 'Preguntas')}</p>
              </div>
              <div className="p-4 bg-white rounded-lg">
                <p className="text-2xl font-bold">80%</p>
                <p className="text-sm text-muted-foreground">{t('To Pass', 'Para Aprobar')}</p>
              </div>
              <div className="p-4 bg-white rounded-lg">
                <p className="text-2xl font-bold">30 min</p>
                <p className="text-sm text-muted-foreground">{t('Time Limit', 'Tiempo')}</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                {t(
                  'Once you start, the timer begins. Make sure you are ready!',
                  'Una vez que inicies, el temporizador comienza. ¡Asegúrate de estar listo!'
                )}
              </p>
            </div>

            <Button 
              size="lg" 
              className="w-full"
              onClick={() => setQuizStarted(true)}
            >
              {t('Start Quiz', 'Iniciar Quiz')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className={passed ? "bg-gradient-to-br from-green-50 to-emerald-50 border-green-200" : "bg-gradient-to-br from-red-50 to-orange-50 border-red-200"}>
          <CardHeader className="text-center">
            {passed ? (
              <>
                <Trophy className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
                <CardTitle className="text-2xl text-green-700">
                  🎉 {t('Congratulations!', '¡Felicidades!')}
                </CardTitle>
                <CardDescription className="text-green-600">
                  {t('You passed the Sandbox Certification Quiz!', '¡Aprobaste el Quiz de Certificación del Sandbox!')}
                </CardDescription>
              </>
            ) : (
              <>
                <XCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
                <CardTitle className="text-2xl text-red-700">
                  {t('Not Passed', 'No Aprobado')}
                </CardTitle>
                <CardDescription className="text-red-600">
                  {t('You need 80% to pass. Review the material and try again.', 'Necesitas 80% para aprobar. Revisa el material e intenta de nuevo.')}
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center">
              <p className="text-5xl font-bold">{percentage.toFixed(0)}%</p>
              <p className="text-muted-foreground">
                {score} / {shuffledQuestions.length} {t('correct', 'correctas')}
              </p>
            </div>

            <Progress value={percentage} className={`h-4 ${passed ? '' : '[&>div]:bg-red-500'}`} />

            <div className="flex gap-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate('/training/sandbox')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('Back to Sandbox', 'Volver al Sandbox')}
              </Button>
              {!passed && (
                <Button 
                  className="flex-1"
                  onClick={restartQuiz}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t('Try Again', 'Intentar de Nuevo')}
                </Button>
              )}
              {passed && (
                <Button 
                  className="flex-1"
                  onClick={() => navigate('/training/final-exam')}
                >
                  {t('Go to Final Exam', 'Ir al Examen Final')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/training/sandbox')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Exit Quiz', 'Salir del Quiz')}
        </Button>
        <Badge variant={timeLeft < 300 ? "destructive" : "outline"} className="text-lg px-4 py-2">
          <Clock className="h-4 w-4 mr-2" />
          {formatTime(timeLeft)}
        </Badge>
      </div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-muted-foreground mb-2">
          <span>{t('Question', 'Pregunta')} {currentQuestion + 1} / {shuffledQuestions.length}</span>
          <span>{progress.toFixed(0)}% {t('complete', 'completado')}</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Question Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {lang === 'es' ? question.question_es : question.question_en}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup 
            key={`question-${currentQuestion}`}
            value={selectedAnswer !== null ? selectedAnswer.toString() : ""}
            onValueChange={(v) => handleAnswerSelect(parseInt(v))}
          >
            {(lang === 'es' ? question.options_es : question.options_en).map((option, index) => (
              <div 
                key={index} 
                className={`flex items-center space-x-3 p-3 rounded-lg transition-colors cursor-pointer ${
                  selectedAnswer === index 
                    ? 'bg-primary/10 border border-primary' 
                    : 'hover:bg-muted/50'
                }`}
                onClick={() => handleAnswerSelect(index)}
              >
                <RadioGroupItem value={index.toString()} id={`option-${currentQuestion}-${index}`} />
                <Label htmlFor={`option-${currentQuestion}-${index}`} className="flex-1 cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button 
          variant="outline" 
          onClick={handlePrevQuestion}
          disabled={currentQuestion === 0}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Previous', 'Anterior')}
        </Button>
        <Button 
          onClick={handleNextQuestion}
          disabled={selectedAnswer === null}
        >
          {currentQuestion === shuffledQuestions.length - 1 
            ? t('Submit', 'Enviar') 
            : t('Next', 'Siguiente')}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
